import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LOCALES, buildLocalizedPath, stripLocalePrefix, useLocale } from '../i18n/LocaleProvider';

const ORIGIN = 'https://romindkids.tech';
const OG_LOCALES = {
  en: 'en_US',
  ru: 'ru_RU',
  es: 'es_ES',
  fr: 'fr_FR',
  ko: 'ko_KR',
  ar: 'ar_AR',
};

const SEO_COPY = {
  en: {
    title: 'ROMIND KIDS — Safe Social Network & Future Academy for Children',
    description: 'ROMIND KIDS is a safer digital ecosystem for children aged 9–14 with protected communication, creative clubs, RONI safety guidance and ROMIND Academy.',
  },
  ru: {
    title: 'ROMIND KIDS — безопасная социальная сеть и Academy для детей',
    description: 'ROMIND KIDS — более безопасная цифровая экосистема для детей 9–14 лет: защищённое общение, творческие клубы, подсказки RONI и ROMIND Academy.',
  },
  es: {
    title: 'ROMIND KIDS — red social segura y Academy para niños',
    description: 'ROMIND KIDS es un ecosistema digital más seguro para niños de 9 a 14 años con comunicación protegida, clubes creativos, orientación de RONI y ROMIND Academy.',
  },
  fr: {
    title: 'ROMIND KIDS — réseau social sûr et Academy pour enfants',
    description: 'ROMIND KIDS est un écosystème numérique plus sûr pour les 9–14 ans avec communication protégée, clubs créatifs, accompagnement RONI et ROMIND Academy.',
  },
  ko: {
    title: 'ROMIND KIDS — 어린이를 위한 안전한 소셜 네트워크와 Academy',
    description: 'ROMIND KIDS는 9–14세 어린이를 위한 더 안전한 디지털 생태계로, 보호된 소통, 창작 클럽, RONI 안전 안내와 ROMIND Academy를 제공합니다.',
  },
  ar: {
    title: 'ROMIND KIDS — شبكة اجتماعية أكثر أمانًا وAcademy للأطفال',
    description: 'ROMIND KIDS منظومة رقمية أكثر أمانًا للأطفال من 9 إلى 14 عامًا، مع تواصل محمي وأندية إبداعية وإرشادات سلامة RONI وROMIND Academy.',
  },
};

const LAB_SEO_COPY = {
  en: { title: 'ROMIND LAB — Coding, AI & Cybersecurity Practice for Children', description: 'Discover ROMIND LAB: hands-on coding, AI, cybersecurity, game development and creative technology for children aged 9–14, with a future pathway through ROMIND Academy.' },
  ru: { title: 'ROMIND LAB — программирование, AI и кибербезопасность для детей', description: 'ROMIND LAB: практические лаборатории программирования, AI, кибербезопасности, разработки игр и цифрового творчества для детей 9–14 лет внутри ROMIND Academy.' },
  es: { title: 'ROMIND LAB — programación, IA y ciberseguridad para niños', description: 'Conoce ROMIND LAB: programación práctica, IA, ciberseguridad, videojuegos y tecnología creativa para niños de 9 a 14 años en ROMIND Academy.' },
  fr: { title: 'ROMIND LAB — code, IA et cybersécurité pour les enfants', description: 'Découvrez ROMIND LAB : pratique du code, de l’IA, de la cybersécurité, de la création de jeux et des technologies créatives pour les 9–14 ans.' },
  ko: { title: 'ROMIND LAB — 어린이를 위한 코딩·AI·사이버보안 실습', description: 'ROMIND LAB에서 9~14세 어린이가 코딩, AI, 사이버보안, 게임 제작, 창의 기술을 직접 실습하며 탐구합니다.' },
  ar: { title: 'ROMIND LAB — البرمجة والذكاء الاصطناعي والأمن للأطفال', description: 'اكتشف ROMIND LAB: مختبرات عملية في البرمجة والذكاء الاصطناعي والأمن السيبراني وصناعة الألعاب للأطفال من 9 إلى 14 عامًا.' },
};

const PRICING_SEO_COPY = {
  en: {
    title: 'ROMIND KIDS Plans & Pricing — 7-Day Free Trial',
    description: 'Compare ROMIND KIDS Messenger, Basic, Plus, All Access and Family plans, including monthly, 6-month and yearly pricing and a 7-day free trial.',
  },
  ru: {
    title: 'Тарифы и цены ROMIND KIDS — 7 дней бесплатно',
    description: 'Сравните тарифы ROMIND KIDS Messenger, Basic, Plus, All Access и Family: цены на месяц, 6 месяцев и год, состав планов и 7-дневный бесплатный пробный период.',
  },
  es: {
    title: 'Planes y precios de ROMIND KIDS — 7 días gratis',
    description: 'Compara Messenger, Basic, Plus, All Access y Family de ROMIND KIDS, con precios mensuales, de 6 meses y anuales y una prueba gratuita de 7 días.',
  },
  fr: {
    title: 'Formules et tarifs ROMIND KIDS — 7 jours gratuits',
    description: 'Comparez Messenger, Basic, Plus, All Access et Family de ROMIND KIDS avec les tarifs mensuels, 6 mois et annuels et un essai gratuit de 7 jours.',
  },
  ko: {
    title: 'ROMIND KIDS 요금제 및 가격 — 7일 무료 체험',
    description: 'ROMIND KIDS Messenger, Basic, Plus, All Access, Family 요금제의 월간·6개월·연간 가격과 포함 기능, 7일 무료 체험을 비교하세요.',
  },
  ar: {
    title: 'خطط وأسعار ROMIND KIDS — تجربة مجانية 7 أيام',
    description: 'قارن خطط ROMIND KIDS Messenger وBasic وPlus وAll Access وFamily مع الأسعار الشهرية و6 أشهر والسنوية وتجربة مجانية لمدة 7 أيام.',
  },
};

const ensureLink = (rel) => {
  let link = document.head.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  return link;
};

const ensurePropertyMeta = (property) => {
  let meta = document.head.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  return meta;
};

const ensureNamedMeta = (name) => {
  let meta = document.head.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  return meta;
};

const LocaleSeo = () => {
  const { locale } = useLocale();
  const location = useLocation();

  useEffect(() => {
    const canonicalPath = stripLocalePrefix(window.location.pathname);
    const currentPath = buildLocalizedPath(locale, canonicalPath);
    const canonicalUrl = `${ORIGIN}${currentPath}`;
    const localizedSet = canonicalPath === '/pricing' ? PRICING_SEO_COPY : canonicalPath === '/romind-lab' ? LAB_SEO_COPY : SEO_COPY;
    const copy = localizedSet[locale] || localizedSet.en;

    document.title = copy.title;
    ensureNamedMeta('description').content = copy.description;
    ensureNamedMeta('twitter:title').content = copy.title;
    ensureNamedMeta('twitter:description').content = copy.description;
    ensurePropertyMeta('og:title').content = copy.title;
    ensurePropertyMeta('og:description').content = copy.description;

    ensureLink('canonical').href = canonicalUrl;

    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => node.remove());
    LOCALES.forEach(({ code }) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = code;
      link.href = `${ORIGIN}${buildLocalizedPath(code, canonicalPath)}`;
      link.dataset.romindHreflang = 'true';
      document.head.appendChild(link);
    });

    const defaultLink = document.createElement('link');
    defaultLink.rel = 'alternate';
    defaultLink.hreflang = 'x-default';
    defaultLink.href = `${ORIGIN}${buildLocalizedPath('en', canonicalPath)}`;
    defaultLink.dataset.romindHreflang = 'true';
    document.head.appendChild(defaultLink);

    ensurePropertyMeta('og:url').content = canonicalUrl;
    ensurePropertyMeta('og:locale').content = OG_LOCALES[locale] || OG_LOCALES.en;

    document.head.querySelectorAll('meta[property="og:locale:alternate"][data-romind-og-locale="true"]').forEach((node) => node.remove());
    LOCALES.filter(({ code }) => code !== locale).forEach(({ code }) => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:locale:alternate');
      meta.content = OG_LOCALES[code];
      meta.dataset.romindOgLocale = 'true';
      document.head.appendChild(meta);
    });
  }, [locale, location.pathname]);

  return null;
};

export default LocaleSeo;
